import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, RotateCw } from 'lucide-react';
import { api } from '@/api/client';
import type { LicenseSummaryResp } from '@/api/types';
import { cn } from '@/lib/utils';

// Mounting this page kicks off a live loadsensor run via
// /v1/measurement-summary/:id, mirroring how v1's /licenseusagesform behaves.
// The endpoint requires an X-Request-Id header even on GET (the handler in
// internal/handler/v1/resource/measurementsummary.go rejects requests
// without one).
export default function LicenseSummaryPage() {
  const { name = '' } = useParams<{ name: string }>();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['license-summary', name],
    queryFn: () =>
      api<LicenseSummaryResp>(
        `/v1/measurement-summary/${encodeURIComponent(name)}`,
        { headers: { 'X-Request-Id': crypto.randomUUID() } },
      ),
    enabled: name !== '',
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    retry: false,
  });

  const summary = data?.license_summary;
  const totalUsage = summary?.total_usage ?? 0;
  const nonBatch = summary?.non_batch_usage ?? 0;
  const total = summary?.total ?? 0;
  const measured = summary?.available ?? 0;
  const users = summary?.users ?? [];
  const hpcUsers = summary?.hpc_users ?? [];

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0"
          >
            <Link
              to="/resources"
              className="hover:text-foreground transition-colors shrink-0"
            >
              Resources
            </Link>
            <ChevronRight
              className="h-3.5 w-3.5 shrink-0 opacity-60"
              aria-hidden
            />
            <span className="text-foreground truncate">License summary</span>
          </nav>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title="Re-run the loadsensor script (may take ~60s)"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 transition-colors shrink-0"
          >
            <RotateCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            Run again
          </button>
        </div>
        <h1
          className="text-2xl font-semibold tracking-tight text-primary font-mono truncate"
          title={name}
        >
          {name}
        </h1>
      </div>

      {isFetching && <ElapsedNotice />}

      {error && (
        <div className="py-3 px-4 rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm">
          Live measurement failed:{' '}
          {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {!isLoading && !error && summary && (
        <>
          <div className="border rounded-lg bg-card p-4">
            <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Summary
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat label="RM Usage" value={totalUsage - nonBatch} />
              <Stat label="Non-RM Usage" value={nonBatch} />
              <Stat label="Total Licenses" value={total} />
              <Stat label="Measured" value={measured} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UserList title="Users" users={users} />
            <UserList title="HPC Users" users={hpcUsers} />
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground font-sans uppercase tracking-wider">
        {label}
      </div>
      <div className="text-2xl font-mono font-semibold mt-1 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function UserList({ title, users }: { title: string; users: string[] }) {
  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      <div className="px-3 py-2 bg-muted/40 border-b flex items-center justify-between">
        <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {title}
        </h2>
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {users.length}
        </span>
      </div>
      {users.length === 0 ? (
        <div className="py-8 text-center text-xs text-muted-foreground">
          None
        </div>
      ) : (
        <ul className="max-h-[40vh] overflow-y-auto divide-y">
          {users.map((u) => (
            <li
              key={u}
              className="px-3 py-1.5 font-mono text-xs break-all"
            >
              {u}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Live elapsed-time counter while the loadsensor runs. Mirrors the v1
// licenseusage.template behavior so users get progress feedback during a
// potentially long script execution.
function ElapsedNotice() {
  const [seconds, setSeconds] = useState(0);
  const start = useRef(Date.now());

  useEffect(() => {
    start.current = Date.now();
    setSeconds(0);
    const id = setInterval(() => {
      setSeconds((Date.now() - start.current) / 1000);
    }, 100);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="py-12 text-center text-muted-foreground text-sm">
      Running live measurement…{' '}
      <span className="font-mono tabular-nums">{seconds.toFixed(1)}s</span>{' '}
      elapsed
    </div>
  );
}
